import { MediaObjectUpload, MediaObjectUrl } from '@kudos/http-client'
import { AttachmentType, useFileMediaUpload } from '@shared/hooks/useFileMediaUpload'
import { useT } from '@transifex/react'
import { FC, useEffect } from 'react'
import type { FieldValues, Path, UseFormClearErrors, UseFormSetError } from 'react-hook-form'
import DocumentAttachmentChip from './DocumentAttachmentChip'
import MediaAttachmentTile from './MediaAttachmentTile'

type AttachmentUploaderProps = {
  fileAttachment?: File | null
  attachmentSrc?: MediaObjectUrl
  index: number
  onError?: (error: Error) => void
  onAttachmentUploaded: (mediaObjectId?: string, attachmentSrc?: MediaObjectUrl) => void
  onDelete?: (mediaObjectId?: string) => void
  attachmentType: AttachmentType
  disabled?: boolean
  mutationFn?: (params: {
    originalFilename: string
    contentType: string
  }) => Promise<MediaObjectUpload>
  setFormError?: UseFormSetError<FieldValues>
  clearFormErrors?: UseFormClearErrors<FieldValues>
  apiErrorFieldPaths?: Path<FieldValues>[]
}
const AttachmentUploader: FC<AttachmentUploaderProps> = ({
  fileAttachment,
  attachmentSrc: src,
  onAttachmentUploaded,
  onDelete,
  onError,
  index,
  attachmentType,
  disabled,
  mutationFn,
  setFormError,
  clearFormErrors,
  apiErrorFieldPaths,
}) => {
  const t = useT()

  const { createObjectUrl, initiateUpload, attachmentSrc, mediaObjectId, isUploading, error } =
    useFileMediaUpload({
      mutationFn,
      setFormError,
      clearFormErrors,
      apiErrorFieldPaths,
    })

  useEffect(() => {
    // do some basic cleanup to revoke the attachments object url
    return () => createObjectUrl()
    // NOTE: SYN-6793 Ignore all exhaustive-deps warnings. Devs should try to fix (and test) when page is revisited
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!!fileAttachment && !isUploading && !mediaObjectId && !error) {
      initiateUpload(fileAttachment)
      createObjectUrl(fileAttachment)
    }
    // NOTE: SYN-6793 Ignore all exhaustive-deps warnings. Devs should try to fix (and test) when page is revisited
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileAttachment, isUploading, mediaObjectId, error])

  useEffect(() => {
    if (!!error && !!onError) {
      onError(error)
    }
    // NOTE: SYN-6793 Ignore all exhaustive-deps warnings. Devs should try to fix (and test) when page is revisited
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error])

  useEffect(() => {
    if (!!mediaObjectId && !!attachmentSrc) {
      onAttachmentUploaded(mediaObjectId, {
        originalFilename: fileAttachment?.name || '',
        url: attachmentSrc,
        variants: [],
      })
    }
    // NOTE: SYN-6793 Ignore all exhaustive-deps warnings. Devs should try to fix (and test) when page is revisited
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaObjectId, attachmentSrc])

  if (attachmentType === 'image' || attachmentType === 'gif') {
    return (
      <MediaAttachmentTile
        mediaObjectId={mediaObjectId}
        isUploading={isUploading}
        onDelete={!!onDelete ? () => onDelete(mediaObjectId) : undefined}
        src={src?.url || attachmentSrc}
        index={index}
        alt={t('Attachment image {index}', { index: index.toLocaleString() })}
        disabled={disabled}
      />
    )
  }

  return (
    <DocumentAttachmentChip
      mediaObjectId={mediaObjectId}
      fileName={fileAttachment?.name || src?.originalFilename}
      isUploading={isUploading}
      onDelete={!!onDelete ? () => onDelete(mediaObjectId) : undefined}
      index={index}
      disabled={disabled}
    />
  )
}

export default AttachmentUploader
